<script lang="ts" setup>
import DOMPurify from 'dompurify';
import type { CurrentQuestion, QuizAnswer } from "@/types/quiz.types";
import { QuestionAnswersType } from "~/enums/quiz.enums";

const { t } = useI18n();

const props = defineProps<{
  readonly?: boolean;
  currentQuestion: CurrentQuestion;
  hideTitle?: boolean;
}>();

const emit = defineEmits<{
  (e: 'change', selectedAnswers: QuizAnswer[]): void;
}>();
const selectedAnswers = ref<QuizAnswer[]>(props.currentQuestion.selectedAnswers);

watch(() => props.currentQuestion, (value, oldValue) => {
  if (value.questionId !== oldValue.questionId) {
    selectedAnswers.value = props.currentQuestion.selectedAnswers;
  }
});

const answers = computed(() => {
  return props.currentQuestion.question?.answers || [];
});

const getAnswerClasses = (answer: QuizAnswer) => {
  return {
    'selected': props.currentQuestion.selectedAnswers.map(i => i.id).includes(answer.id),
    'correct': props.currentQuestion.isRevealingAnswer && props.currentQuestion.correctAnswers.includes(answer.id),
    'incorrect': props.currentQuestion.isRevealingAnswer && !props.currentQuestion.correctAnswers.includes(answer.id),
  };
};

const handleSelectionChange = (e: Event, answer: QuizAnswer) => {
  if (props.currentQuestion.question.type === QuestionAnswersType.Single) {
    selectedAnswers.value = [answer];
  }

  emit('change', selectedAnswers.value);
};

const formatAnswer = (answer: QuizAnswer) => {
  return DOMPurify.sanitize(answer.answer || answer.title || '');
};

const title = computed(() => {
  if (props.readonly) {
    return t('course.quiz.question.answer.placeholder.yourAnswer');
  }

  return props.currentQuestion.question.type === QuestionAnswersType.Multiple
    ? t('course.quiz.question.answer.placeholder.selectAllThatApply')
    : t('course.quiz.question.answer.placeholder.selectOne');
});
</script>

<template>
  <div
    class="quiz-answers"
    :class="[
      {'pointer-events-none': currentQuestion.isRevealingAnswer},
      {'quiz-answers--readonly': readonly}
    ]"
  >
    <div
      v-if="!hideTitle"
      class="text-14 uppercase tracking-widest text-neutral-90 font-bold mb-4"
    >
      {{title}}
    </div>

    <div class="flex flex-col gap-4">
      <template
        v-for="answer in answers"
        :key="answer.id"
      >
        <label>
          <Card
            :pt="{
              root: {
                class: [
                  'rounded-xl shadow-medmastery transition-all relative',
                  {'cursor-pointer': !readonly},
                  getAnswerClasses(answer)
                ]
              },
              content: {
                class: 'p-0'
              }
            }"
          >
            <template #content>
              <Badge
                v-if="currentQuestion.isRevealingAnswer && currentQuestion.correctAnswers.includes(answer.id)"
                :value="t('course.quiz.result.correctAnswer')"
                class="absolute -top-2.5 right-4 uppercase font-bold bg-blue-60 !leading-normal !h-auto py-1 rounded-lg"
              />

              <div class="flex items-center gap-4">
                <RadioButton
                  v-if="currentQuestion.question.type === QuestionAnswersType.Single"
                  v-model="selectedAnswers[0]"
                  :disabled="currentQuestion.isRevealingAnswer || readonly"
                  :value="answer"
                  @change="(e: Event) => handleSelectionChange(e, answer)"
                />

                <Checkbox
                  v-else
                  v-model="selectedAnswers"
                  :value="answer"
                  :disabled="currentQuestion.isRevealingAnswer || readonly"
                  @change="(e: Event) => handleSelectionChange(e, answer)"
                />

                <span v-sanitize-html="formatAnswer(answer)" />
              </div>
            </template>
          </Card>
        </label>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@import '@mui/assets/styles/font-styles.scss';

.quiz-answers {
  &:not(.quiz-answers--readonly) {
    .p-card:hover {
      border-color: var(--palette-blue-60);
    }
  }

  .p-card {
    border: 2px solid transparent;

    &.selected {
      border-color: var(--palette-blue-60) !important;
      background-color: var(--palette-blue-10);
    }

    &.selected.correct {
      background-color: var(--palette-green-10) !important;
      border-color: transparent !important;
    }

    &.selected.incorrect {
      background-color: var(--palette-red-10) !important;
      border-color: transparent !important;
    }

    .p-checkbox.p-highlight .p-checkbox-box {
      background: var(--palette-blue-60) !important;
      border-color: var(--palette-blue-60) !important;
    }
  }
}
</style>